// @/components/page/Growth/Dashboard/GrowthDashboardSoilLayerMoistureCard.tsx

import { useEffect, useState } from 'react';
import Tooltip from '@components/common/Tooltip';
import { api } from '@/api/growth/dashboard/api';
import { SoilLayerMoistureItem } from '@/api/growth/dashboard/type';

interface GrowthDashboardSoilLayerMoistureCardProps {
    gfIdx: number;
}

const GrowthDashboardSoilLayerMoistureCard = ({
    gfIdx,
}: GrowthDashboardSoilLayerMoistureCardProps) => {
    const [loading, setLoading] = useState(true);
    const [soilLayerData, setSoilLayerData] = useState<SoilLayerMoistureItem[]>(
        [],
    );

    useEffect(() => {
        if (gfIdx) {
            fetchSoilLayerMoisture(gfIdx);
        }
    }, [gfIdx]);

    const fetchSoilLayerMoisture = async (gfIdx: number) => {
        try {
            setLoading(true);
            const response = await api.getSoilLayerMoistureCardInfo({ gfIdx });
            // response.data가 배열
            setSoilLayerData(response.data || []);
        } catch (error) {
            console.error('토양층수분 정보 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    // 수분상태에 따른 뱃지 색상 반환
    const getMoistureStatusBadge = (status: string) => {
        const statusMap: { [key: string]: string } = {
            가뭄: 'bg-danger',
            부족: 'bg-pink',
            적정: 'bg-success',
            과다: 'bg-info',
            침수: 'bg-primary',
        };
        return statusMap[status] || 'bg-secondary';
    };

    // 토양층에 따른 배경색 클래스 반환
    const getSoilLayerClass = (index: number) => {
        return `bg-table-soil${index + 1} soil-row`;
    };

    return (
        <div className="custom-card overflow-hidden card">
            <div className="justify-content-between card-header">
                <div className="card-title">
                    토양층수분{' '}
                    <Tooltip
                        content="토양 각 층별 수분 상태와 수분량을 보여줍니다"
                        customClass="tooltip-dark"
                        placement="top"
                    >
                        <i className="ri-question-line" />
                    </Tooltip>
                </div>
            </div>
            <div className="p-0 card-body">
                {loading ? (
                    <div className="text-center p-4">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">로딩중...</span>
                        </div>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="text-nowrap table">
                            <thead>
                                <tr>
                                    <th>토양층</th>
                                    <th>깊이</th>
                                    <th>수분상태</th>
                                    <th>수분량</th>
                                </tr>
                            </thead>
                            <tbody>
                                {soilLayerData.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="text-center">
                                            데이터가 없습니다
                                        </td>
                                    </tr>
                                ) : (
                                    soilLayerData.map((item, index) => (
                                        <tr
                                            key={index}
                                            className={getSoilLayerClass(index)}
                                        >
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <div>{item.soilLayer}</div>
                                                </div>
                                            </td>
                                            <td className="fw-semibold fs-15">
                                                {item.depth}
                                            </td>
                                            <td>
                                                <span
                                                    className={`fw-medium badge fs-11 ${getMoistureStatusBadge(
                                                        item.moistureStatus,
                                                    )}`}
                                                >
                                                    {item.moistureStatus}
                                                </span>
                                            </td>
                                            <td className="fw-medium">
                                                {item.moistureAmount}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GrowthDashboardSoilLayerMoistureCard;
